import React, { useState, useEffect, useRef } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import {
  Building2,
  Plus,
  X,
  Search,
  Shield,
  Check
} from 'lucide-react';

/**
 * TrainingProvidersContent - Manages training provider selection for draft quotes
 * Shows a table of selected providers with search and add functionality
 */
const TrainingProvidersContent = ({ booking, onRefresh, onSaveStateChange }) => {
  const { userProfile, company } = useAuth();
  const [saving, setSaving] = useState(false);
  const autoSaveTimeoutRef = useRef(null);
  const isInitializedRef = useRef(false);

  // Training providers state
  const [globalTrainers, setGlobalTrainers] = useState([]);
  const [companyTrainers, setCompanyTrainers] = useState([]);
  const [selectedTrainers, setSelectedTrainers] = useState([]);
  const [hasUserModifiedTrainers, setHasUserModifiedTrainers] = useState(false);
  const [trainerSearch, setTrainerSearch] = useState('');

  // New provider form
  const [newProviderName, setNewProviderName] = useState('');
  const [newProviderEmail, setNewProviderEmail] = useState('');
  const [isAddingProvider, setIsAddingProvider] = useState(false);

  // Load training providers
  useEffect(() => {
    fetchTrainingProviders();
  }, [company?.id]);

  // Load selected trainers from booking, or auto-select BOHSA verified trainers if none exist
  useEffect(() => {
    if (hasUserModifiedTrainers) return;

    if (booking?.selected_trainers && booking.selected_trainers.length > 0) {
      setSelectedTrainers(booking.selected_trainers);
      // Notify parent that providers exist from DB
      onSaveStateChange?.(false, null, { hasProviders: true });
    } else if (globalTrainers.length > 0) {
      // Auto-select BOHSA verified trainers when no trainers are saved on the quote
      const defaultTrainers = globalTrainers.map(trainer => ({
        id: trainer.id,
        name: trainer.trainer_name,
        email: trainer.booking_email,
        type: 'global',
        wasNotified: false
      }));
      setSelectedTrainers(defaultTrainers);
      setHasUserModifiedTrainers(true);

      // Immediately save auto-selected trainers to DB and notify parent
      const saveAutoSelected = async () => {
        try {
          await supabase
            .from('training_quotes')
            .update({
              selected_trainers: defaultTrainers,
              updated_date: new Date().toISOString()
            })
            .eq('id', booking.id);
          onSaveStateChange?.(false, new Date(), { hasProviders: true });
        } catch (err) {
          console.error('Failed to auto-save trainers:', err);
        }
      };
      saveAutoSelected();
    }
  }, [booking?.selected_trainers, hasUserModifiedTrainers, globalTrainers]);

  // Mark as initialized after first render
  useEffect(() => {
    const timer = setTimeout(() => {
      isInitializedRef.current = true;
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  // Auto-save when selected trainers change
  useEffect(() => {
    if (!isInitializedRef.current || !hasUserModifiedTrainers) return;

    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    autoSaveTimeoutRef.current = setTimeout(() => {
      handleSave();
    }, 1000);

    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, [selectedTrainers, hasUserModifiedTrainers]);

  const fetchTrainingProviders = async () => {
    try {
      // Fetch global trainers (no company_id)
      const { data: global, error: globalError } = await supabase
        .from('trainers')
        .select('*')
        .is('company_id', null)
        .order('display_order', { nullsLast: true })
        .order('trainer_name');

      if (!globalError && global) {
        setGlobalTrainers(global);
      }

      // Fetch company trainers if company exists
      if (company?.id) {
        const { data: companyData, error: companyError } = await supabase
          .from('trainers')
          .select('*')
          .eq('company_id', company.id)
          .order('display_order', { nullsLast: true })
          .order('trainer_name');

        if (!companyError && companyData) {
          setCompanyTrainers(companyData);
        }
      }
    } catch (error) {
      console.error('Error fetching training providers:', error);
    }
  };

  const handleToggleTrainer = (trainer) => {
    const isSelected = selectedTrainers.some(t => t.id === trainer.id);
    setHasUserModifiedTrainers(true);

    if (isSelected) {
      setSelectedTrainers(prev => prev.filter(t => t.id !== trainer.id));
    } else {
      const trainerType = trainer.company_id === null ? 'global' : 'additional';
      const trainerObj = {
        id: trainer.id,
        name: trainer.trainer_name,
        email: trainer.booking_email,
        type: trainerType,
        wasNotified: false
      };
      setSelectedTrainers(prev => [...prev, trainerObj]);
    }
  };

  const handleRemoveSelectedTrainer = (trainerId) => {
    setHasUserModifiedTrainers(true);
    setSelectedTrainers(prev => prev.filter(t => t.id !== trainerId));
  };

  const handleAddNewProvider = async () => {
    if (!newProviderName.trim()) {
      toast.error('Please enter provider name');
      return;
    }
    if (!newProviderEmail.trim()) {
      toast.error('Please enter provider email');
      return;
    }
    if (!userProfile?.id) {
      toast.error('User profile not found');
      return;
    }

    try {
      const { data: newTrainer, error } = await supabase
        .from('trainers')
        .insert({
          trainer_name: newProviderName.trim(),
          booking_email: newProviderEmail.trim(),
          company_id: company?.id || null,
          created_by: userProfile.id,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) throw error;

      // Add to company trainers list
      setCompanyTrainers(prev => [...prev, newTrainer]);

      // Auto-select the new trainer
      const trainerObj = {
        id: newTrainer.id,
        name: newTrainer.trainer_name,
        email: newTrainer.booking_email,
        type: 'additional',
        wasNotified: false
      };
      setHasUserModifiedTrainers(true);
      setSelectedTrainers(prev => [...prev, trainerObj]);

      // Reset form
      setNewProviderName('');
      setNewProviderEmail('');
      setIsAddingProvider(false);
      toast.success('Training provider added and selected');
    } catch (error) {
      console.error('Error adding provider:', error);
      toast.error(`Failed to add provider: ${error.message || 'Unknown error'}`);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    onSaveStateChange?.(true);
    try {
      const { error } = await supabase
        .from('training_quotes')
        .update({
          selected_trainers: selectedTrainers,
          updated_date: new Date().toISOString()
        })
        .eq('id', booking.id);

      if (error) throw error;

      const now = new Date();
      // Report validation state along with save completion
      onSaveStateChange?.(false, now, { hasProviders: selectedTrainers.length > 0 });
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Failed to save changes');
      onSaveStateChange?.(false);
    } finally {
      setSaving(false);
    }
  };

  // Filter available trainers
  const availableTrainers = [...globalTrainers, ...companyTrainers]
    .filter(trainer => {
      // Exclude already selected
      if (selectedTrainers.some(st => st.id === trainer.id)) return false;
      // Apply search filter
      if (trainerSearch.trim()) {
        const search = trainerSearch.toLowerCase();
        return (
          trainer.trainer_name.toLowerCase().includes(search) ||
          (trainer.booking_email && trainer.booking_email.toLowerCase().includes(search))
        );
      }
      return true;
    });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900">Training Providers</h3>
        <p className="text-sm text-gray-500">
          Select providers to receive quote requests. Verified providers are directly integrated.
        </p>
      </div>

      {/* Selected Providers Table */}
      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Provider Name</TableHead>
              <TableHead>Contact Email</TableHead>
              <TableHead className="w-[100px] text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {selectedTrainers.map(trainer => (
              <TableRow key={trainer.id}>
                <TableCell className="font-medium">
                  <div className="flex items-center gap-2">
                    {trainer.name}
                    {trainer.type === 'global' && (
                      <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-300 px-2 py-0.5 text-xs">
                        <Shield className="h-3 w-3 mr-1" />
                        Verified
                      </Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-sm text-gray-600">
                  {trainer.email || 'N/A'}
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveSelectedTrainer(trainer.id)}
                    className="h-8 px-2 text-gray-500 hover:text-red-600"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}

            {selectedTrainers.length === 0 && (
              <TableRow>
                <TableCell colSpan={3} className="text-center text-gray-500 py-8">
                  No training providers selected
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Add Providers Section */}
      <div className="space-y-4">
        <Label className="text-sm font-medium">Select Training Providers</Label>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            value={trainerSearch}
            onChange={(e) => setTrainerSearch(e.target.value)}
            placeholder="Search providers..."
            className="pl-9"
          />
        </div>

        {/* Available Providers List */}
        <div className="space-y-1.5 max-h-[280px] overflow-y-auto border rounded-lg p-2 bg-gray-50">
          {availableTrainers.map(trainer => (
            <div
              key={trainer.id}
              onClick={() => handleToggleTrainer(trainer)}
              className="flex items-center gap-2 p-3 rounded-lg hover:bg-white cursor-pointer transition-colors border border-transparent hover:border-gray-200"
            >
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium">{trainer.trainer_name}</p>
                  {trainer.company_id === null && (
                    <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-300 px-2 py-0.5 text-xs">
                      <Shield className="h-3 w-3 mr-1" />
                      Verified
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-gray-500">{trainer.booking_email || 'No email'}</p>
              </div>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-8 px-3"
              >
                <Plus className="h-3 w-3 mr-1" />
                Add
              </Button>
            </div>
          ))}
          {availableTrainers.length === 0 && (
            <p className="text-sm text-gray-500 text-center py-6">
              {trainerSearch.trim() ? 'No providers found matching your search' : 'All providers have been selected'}
            </p>
          )}
        </div>
      </div>

      {/* Add New Provider */}
      <div className="space-y-3">
        <Label className="text-sm font-medium">Add New Provider to Company</Label>
        {!isAddingProvider ? (
          <Button
            type="button"
            variant="outline"
            onClick={() => setIsAddingProvider(true)}
            className="w-full"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Training Provider
          </Button>
        ) : (
          <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border">
            <Input
              value={newProviderName}
              onChange={(e) => setNewProviderName(e.target.value)}
              placeholder="Provider Name"
              className="flex-1"
            />
            <Input
              type="email"
              value={newProviderEmail}
              onChange={(e) => setNewProviderEmail(e.target.value)}
              placeholder="Booking Email"
              className="flex-1"
            />
            <Button
              type="button"
              size="icon"
              onClick={handleAddNewProvider}
            >
              <Check className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() => {
                setIsAddingProvider(false);
                setNewProviderName('');
                setNewProviderEmail('');
              }}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrainingProvidersContent;
